import { isFrameDue } from './frame-budget';
import { paintBackground } from './territory-background';
import { createWalkers, paintScene, readPalette, type SceneState } from './territory-paint';

/** The living territory behind the sign-in card: lifecycle of the canvas (size, animation
 *  loop, pauses). With reduced motion, or in a hidden tab, it draws one still frame. While it
 *  moves it paints at most 30 frames a second over a cached background (B-18): a command-post PC
 *  keeps its CPU for the Control Center open next to it. */

export interface Territory {
  /** Highlights the recognised organisation (zoom, stronger sweep). */
  focus(on: boolean): void;
  /** Re-reads the tokens after a theme change. */
  repaint(): void;
  destroy(): void;
}

const NARROW = 1100;
const STILL_TIME = 8;
const FOCUS_ZOOM = 1.22;
// Share of the remaining zoom covered at each painted frame: 0.12 at 30 fps matches the old 0.06
// at 60 fps (1 − 0.94²), so the zoom on a recognised organization lasts the same.
const ZOOM_EASING = 0.12;

/** Where the mark sits: the free area left of the desk, or top right under 1100 px. */
export function sceneNode(width: number, height: number): { x: number; y: number; r: number } {
  if (window.innerWidth <= NARROW) return { x: width * 0.82, y: 150, r: width * 0.45 };
  const area = width - Math.min(520, Math.max(360, width * 0.36));
  return { x: area * 0.6, y: height * 0.63, r: Math.min(area, height) * 0.42 };
}

export function createTerritory(canvas: HTMLCanvasElement): Territory {
  const cx = canvas.getContext('2d');
  if (cx === null) return { focus() {}, repaint() {}, destroy() {} };
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const started = performance.now();
  const scene: SceneState = {
    cx,
    palette: readPalette(),
    size: { w: 0, h: 0 },
    node: { x: 0, y: 0, r: 300 },
    zoom: 1,
    focused: false,
    walkers: createWalkers(),
  };
  const background = document.createElement('canvas');
  let dpr = 1;
  let zoomTarget = 1;
  let frame = 0;
  let lastPaint = Number.NEGATIVE_INFINITY;

  function layout(): void {
    const rect = canvas.getBoundingClientRect();
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    scene.size = { w: rect.width, h: rect.height };
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    cx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    scene.node = sceneNode(rect.width, rect.height);
    paintBackground(background, scene, dpr);
  }

  function draw(now: number): void {
    const still = reduce.matches;
    scene.zoom += (zoomTarget - scene.zoom) * (still ? 1 : ZOOM_EASING);
    paintScene(scene, background, still ? STILL_TIME : (now - started) / 1000, still);
  }

  function loop(now: number): void {
    if (isFrameDue(now, lastPaint)) {
      lastPaint = now;
      draw(now);
    }
    frame = requestAnimationFrame(loop);
  }

  function start(): void {
    cancelAnimationFrame(frame);
    if (reduce.matches || document.hidden) draw(performance.now());
    else frame = requestAnimationFrame(loop);
  }

  const onResize = () => {
    layout();
    draw(performance.now());
  };
  window.addEventListener('resize', onResize);
  document.addEventListener('visibilitychange', start);
  reduce.addEventListener('change', start);
  layout();
  start();

  return {
    focus(on) {
      scene.focused = on;
      zoomTarget = on ? FOCUS_ZOOM : 1;
      if (reduce.matches) draw(performance.now());
    },
    repaint() {
      scene.palette = readPalette();
      paintBackground(background, scene, dpr);
      draw(performance.now());
    },
    destroy() {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', start);
      reduce.removeEventListener('change', start);
    },
  };
}
